import { apiErrorMessage } from '@agos/api-client'
import { formatCents, parseMoneyToCents, tt } from '@agos/shared'

import type { WheelSegment } from './wheel'

/**
 * The wheel's campaign, as `GET /campaigns/wheel` answers it.
 *
 * **Measured on this data: `is_active` is false and there are 2 640 spins in the history.** So the
 * wheel is switched off today and has been used a great deal, which is exactly the case a port must not
 * skip — a franchise turning it back on would otherwise find the customer app has no wheel at all.
 *
 * The reading is defensive because every field is a franchise's own setting and one of them decides
 * what a customer is given: a segment with no label or an unreadable value is dropped rather than drawn,
 * because a slice the wheel can stop on and cannot name is a prize nobody can claim.
 */

export interface WheelPrizeSegment extends WheelSegment {
  /** `percent`, `fixed`, `no_win`, or whatever else a franchise configured. */
  type: string
  value: number
}

export interface WheelCampaign {
  active: boolean
  segments: WheelPrizeSegment[]
  /** What the basket must reach before a spin is offered, in cents. `null` when unset. */
  minOrderCents: number | null
  rewardValidityHours: number | null
  canSpin: boolean
  /** When the next spin becomes available, as the server said it. */
  nextSpinAt: string | null
  lastSpin: { segmentIndex: number; couponCode: string | null } | null
}

const FALLBACK_COLOURS = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6', '#8b5cf6', '#ec4899', '#6b7280']

export function wheelCampaignOf(payload: unknown): WheelCampaign | null {
  if (typeof payload !== 'object' || payload === null) return null
  const row = payload as Record<string, unknown>
  const rawSegments = Array.isArray(row.segments) ? row.segments : []

  const segments: WheelPrizeSegment[] = []
  for (const [index, raw] of rawSegments.entries()) {
    if (typeof raw !== 'object' || raw === null) continue
    const segment = raw as Record<string, unknown>
    const label = typeof segment.label === 'string' ? segment.label.trim() : ''
    // A slice the wheel can stop on and cannot name is a prize nobody can claim.
    if (label === '') continue
    segments.push({
      label,
      color:
        typeof segment.color === 'string' && segment.color.trim() !== ''
          ? segment.color.trim()
          : (FALLBACK_COLOURS[index % FALLBACK_COLOURS.length] ?? '#6b7280'),
      type: typeof segment.type === 'string' ? segment.type : 'no_win',
      value: typeof segment.value === 'number' && Number.isFinite(segment.value) ? segment.value : 0,
    })
  }

  const last = typeof row.lastSpin === 'object' && row.lastSpin !== null ? (row.lastSpin as Record<string, unknown>) : null

  return {
    active: row.isActive === true,
    segments,
    minOrderCents:
      typeof row.minOrderAmount === 'string' || typeof row.minOrderAmount === 'number'
        ? parseMoneyToCents(row.minOrderAmount)
        : null,
    rewardValidityHours:
      typeof row.rewardValidityHours === 'number' && Number.isFinite(row.rewardValidityHours)
        ? row.rewardValidityHours
        : null,
    canSpin: row.canSpin === true,
    nextSpinAt: typeof row.nextSpinAt === 'string' ? row.nextSpinAt : null,
    lastSpin:
      last === null || typeof last.segmentIndex !== 'number'
        ? null
        : {
            segmentIndex: last.segmentIndex,
            couponCode: typeof last.couponCode === 'string' ? last.couponCode : null,
          },
  }
}

/**
 * Whether the wheel is worth drawing at all.
 *
 * Legacy's own gate: a signed-in customer, an active campaign, and something to draw. Two segments is
 * the minimum that is recognisably a wheel — one slice is a circle, and it cannot be lost.
 */
export function wheelIsOffered(campaign: WheelCampaign | null, signedIn: boolean): boolean {
  return signedIn && campaign !== null && campaign.active && campaign.segments.length >= 2
}

/** What the wheel says under itself: the terms, or why there is no spin right now. */
export function wheelNote(campaign: WheelCampaign): string {
  if (campaign.canSpin) {
    if (campaign.minOrderCents !== null && campaign.minOrderCents > 0) {
      return `${tt('Platí pri objednávke nad')} ${formatCents(campaign.minOrderCents)}.`
    }
    return tt('Zatočte a vyhrajte zľavu na ďalšiu objednávku.')
  }
  // A cooldown is not a note — it is a countdown, and `countdownTo` renders it. Legacy says *Ďalší spin
  // o* above a live `hh:mm:ss`; an earlier draft here invented *Ďalšie točenie bude dostupné neskôr.*,
  // which §8 does not allow and which tells a customer nothing they can plan around.
  if (campaign.nextSpinAt !== null) return ''
  return tt('Točenie momentálne nie je dostupné.')
}

/**
 * How long until the wheel can be spun again, as `hh:mm:ss`.
 *
 * Legacy's `Countdown` recomputes this every **second** and this copies both the format and the interval
 * (§14.1). `null` when there is nothing to count down to; `00:00:00` once the moment has passed, which is
 * legacy's own floor — the server decides whether a spin is allowed, and a countdown that went negative
 * would be arguing with it.
 */
export function countdownTo(nextSpinAt: string | null, now: number): string | null {
  if (nextSpinAt === null) return null
  const target = new Date(nextSpinAt).getTime()
  if (Number.isNaN(target)) return null
  const remaining = target - now
  if (remaining <= 0) return '00:00:00'
  const pad = (value: number) => String(value).padStart(2, '0')
  return [
    pad(Math.floor(remaining / 3_600_000)),
    pad(Math.floor((remaining % 3_600_000) / 60_000)),
    pad(Math.floor((remaining % 60_000) / 1_000)),
  ].join(':')
}

/**
 * Why a spin was refused, in the server's own words - §9.361, FB-646.
 *
 * **`WheelService.spin` refuses in three worded ways** and one of them is *"Dnes ste už točili.
 * Skúste znova zajtra."* (`WheelService.java:154`), beside *"Koleso šťastia nie je aktívne"* twice.
 * The screen used to answer all three with *"Točenie sa nepodarilo. Skúste to znova."* - telling a
 * customer who has spent today's go to do the one thing that cannot work, for the next twenty-four
 * hours.
 *
 * So the refusal is repeated verbatim. It is written for a customer and it already says whether the
 * campaign is off or the go is used up, which is more than this file could work out.
 *
 * **The fallback deliberately does not say *try again*.** A refusal with no sentence is most likely
 * the same spent-go case, and `ProfilePage.play` - the mini-game's version of this, which has always
 * been right - answers an absent `nextPlayAt` with *"Dnešné hranie ste vyčerpali"* rather than with
 * advice it cannot stand behind.
 *
 * A **malformed answer** is a different failure and keeps *try again*; see `spinBrokenNote`.
 */
export function spinRefusal(failure: unknown): string {
  return apiErrorMessage(failure, tt('Točenie sa nepodarilo.'))
}

/**
 * A 2xx that carried no segment - the one case where *try again* is honest advice.
 *
 * Kept apart from `spinRefusal` because the two call for different things: the server has not refused
 * anything here, it has answered something this screen cannot animate, and repeating a click is a
 * reasonable response to that.
 */
export function spinBrokenNote(): string {
  return tt('Točenie sa nepodarilo. Skúste to znova.')
}

/** What a spin won, in the customer's words. `''` for a segment that is not a win. */
export function prizeNoteOf(segment: WheelPrizeSegment | undefined, couponCode: string | null): string {
  if (segment === undefined) return ''
  if (segment.type === 'no_win' || segment.value === 0) return tt('Tentokrát to nevyšlo. Skúste to nabudúce.')
  const code = couponCode === null || couponCode.trim() === '' ? '' : ` ${tt('Kód')}: ${couponCode.trim()}`
  return `${segment.label}.${code}`
}

/** The segment a spin landed on, or `undefined` when the server named one the wheel does not have. */
export function landedOn(campaign: WheelCampaign, index: unknown): WheelPrizeSegment | undefined {
  if (typeof index !== 'number' || !Number.isInteger(index)) return undefined
  return campaign.segments[index]
}
